import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Circle, Lock, Clock, FileText, Video, ChevronDown, ChevronUp, ExternalLink, Send, Download, Users, Info, Edit, Eye, RotateCcw, Mail, FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DocumentPreviewDialog } from "@/components/documents/DocumentPreviewDialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DocumentGenerateDialog } from "@/components/documents/DocumentGenerateDialog";
import SignatureRequestWizard from "@/components/signatures/SignatureRequestWizard";
import { SignatureStatusBadge } from "@/components/signatures/SignatureStatusBadge";
import { SignatureTrackingDialog } from "@/components/signatures/SignatureTrackingDialog";
import DocumentEmailWizard from "@/components/documents/DocumentEmailWizard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { generateDocumentPdf } from "@/lib/pdfGeneration";
// CRITICAL FIX #2 & #7: Import Meeting components
import { MeetingRecordingInterface } from "@/components/meetings/MeetingRecordingInterface";
import { MeetingMinutesReview } from "@/components/meetings/MeetingMinutesReview";
import { DistributeAndArchivePhase } from "@/components/meetings/DistributeAndArchivePhase";
import { AssetTransfersPhase1 } from "@/components/workflows/asset-transfers-contributions/AssetTransfersPhase1";
import AssetTransfersPhase2 from "@/components/workflows/asset-transfers-contributions/AssetTransfersPhase2";
import { AssetTransfersPhase3 } from "@/components/workflows/asset-transfers-contributions/AssetTransfersPhase3";
import { OfficerPhase1 } from "@/components/workflows/officer/OfficerPhase1";
import OfficerPhase2 from "@/components/workflows/officer/OfficerPhase2";
import { OfficerPhase3 } from "@/components/workflows/officer/OfficerPhase3";
import { MemberPhase1 } from "@/components/workflows/member/MemberPhase1";
import MemberPhase2 from "@/components/workflows/member/MemberPhase2";
import { MemberPhase3 } from "@/components/workflows/member/MemberPhase3";
import { ResolutionPhase1 } from "@/components/workflows/resolution/ResolutionPhase1";
import ResolutionPhase2 from "@/components/workflows/resolution/ResolutionPhase2";
import { ConductingMeetingPhase1 } from "@/components/workflows/conducting-meeting/ConductingMeetingPhase1";
import { ConductMeetingPhase2 } from "@/components/workflows/conducting-meeting/ConductMeetingPhase2";
import { ConductMeetingPhase3 } from "@/components/workflows/conducting-meeting/ConductMeetingPhase3";
import { ConductMeetingPhase4 } from "@/components/workflows/conducting-meeting/ConductMeetingPhase4";
import { CovenantPhase1 } from "@/components/workflows/covenant/CovenantPhase1";
import { CovenantPhase2 } from "@/components/workflows/covenant/CovenantPhase2";
import { HousingPhase1 } from "@/components/workflows/housing/HousingPhase1";
import { HousingPhase2 } from "@/components/workflows/housing/HousingPhase2";
import { GivingPhase1 } from "@/components/workflows/giving/GivingPhase1";
import { GivingPhase2 } from "@/components/workflows/giving/GivingPhase2";
import { ExitPhase1 } from "@/components/workflows/exit/ExitPhase1";
import { ExitPhase2 } from "@/components/workflows/exit/ExitPhase2";
import { AppointPhase1 } from "@/components/workflows/appoint/AppointPhase1";
import { AppointPhase2 } from "@/components/workflows/appoint/AppointPhase2";

import { RecordJournalPhase1 } from "@/components/workflows/record-journal/RecordJournalPhase1";
import { RecordJournalPhase2 } from "@/components/workflows/record-journal/RecordJournalPhase2";
import { DocumentRequirementsGrid } from "@/components/workflows/DocumentRequirementsGrid";
import { DocumentDistributionPanel } from "@/components/workflows/DocumentDistributionPanel";
import { GovernanceFilingPanel } from "@/components/workflows/GovernanceFilingPanel";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Phase {
  phase_number: number;
  phase_name: string;
  description: string;
  tasks: string[];
  documents?: Array<{
    template_id: string;
    title: string;
    required: boolean;
  }>;
  document_requirements?: Array<string | { template_key: string; auto_generate?: boolean; [key: string]: any }>;
  training_videos?: string[];
  estimated_time: string;
  requirements?: Array<{
    id: string;
    description: string;
    completed: boolean;
  }>;
}

// Helper functions to normalize document_requirements (can be strings or objects)
const isReqObject = (req: any): req is { template_key: string; [key: string]: any } => {
  return typeof req === 'object' && req !== null && 'template_key' in req;
};

const getReqKey = (req: string | { template_key: string; [key: string]: any }): string => {
  return isReqObject(req) ? req.template_key : req;
};

const getReqName = (
  req: string | { template_key: string; document_name?: string; [key: string]: any }, 
  keyToNameMap: Record<string, string>
): string => {
  // ✅ PRIORITY 1: Use document_name from req object (from workflow definition)
  if (isReqObject(req) && req.document_name) {
    return req.document_name;
  }
  
  // PRIORITY 2: Fall back to hardcoded map (legacy support)
  const key = getReqKey(req);
  return keyToNameMap[key] || key;
};

const normalizeRequirement = (req: string | { template_key: string; [key: string]: any }) => {
  if (isReqObject(req)) {
    return req; // Already has template_key
  }
  return { template_key: req }; // String -> object with template_key
};

interface WorkflowPhaseManagerProps {
  taskId: string;
  workflowId: string;
  unaId: string;
  onPhaseComplete: () => void;
  onPhaseChange: (phaseNumber: number) => void;
}

export const WorkflowPhaseManager = ({
  taskId,
  workflowId,
  unaId,
  onPhaseComplete,
  onPhaseChange,
}: WorkflowPhaseManagerProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [phases, setPhases] = useState<Phase[]>([]);
  const [workflow, setWorkflow] = useState<any>(null);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [phaseStatuses, setPhaseStatuses] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState<Record<number, boolean>>({});
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState<Record<string, any>>({});
  const [generatedDocuments, setGeneratedDocuments] = useState<Record<string, any>>({});
  const [templatesByKey, setTemplatesByKey] = useState<Record<string, any>>({});
  // CRITICAL FIX #2: Add state for meeting data
  const [meetingMinutesId, setMeetingMinutesId] = useState<string | null>(null);
  const [workflowData, setWorkflowData] = useState<Record<string, any>>({});
  // Signature workflow state
  const [signatureRequests, setSignatureRequests] = useState<Record<string, any>>({});
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocumentForSignature, setSelectedDocumentForSignature] = useState<string | null>(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedDocumentIdForPreview, setSelectedDocumentIdForPreview] = useState<string | null>(null);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [selectedResendDoc, setSelectedResendDoc] = useState<{ documentId: string; signatureRequestId: string } | null>(null);
  // Email wizard state
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocumentForEmail, setSelectedDocumentForEmail] = useState<string | null>(null);
  const [emailDeliveries, setEmailDeliveries] = useState<Record<string, any>>({});
  const [workflowMemberData, setWorkflowMemberData] = useState<{ name: string; email: string } | undefined>(undefined);
  
  // Phase 2 state for "Conducting a Meeting" workflow
  const [phase2DistributionComplete, setPhase2DistributionComplete] = useState(false);
  const [phase2FilingComplete, setPhase2FilingComplete] = useState(false);
  const [phase2Blockers, setPhase2Blockers] = useState<string[]>([]);
  
  // Task user_id for asset transfers workflow
  const [taskUserId, setTaskUserId] = useState<string | null>(null);

  const TEMPLATE_KEY_TO_NAME: Record<string, string> = {
    // Member Management Templates
    form_member_information: 'Member Information Collection Form',
    resolution_member_appointment: 'Member Appointment Resolution',
    letter_member_acceptance: 'Member Acceptance Letter',
    cover_member_records_filing: 'Member Records Filing Cover Sheet',
    record_member_roster_update: 'Member Roster Update Record',
    packet_new_member_welcome: 'New Member Welcome Packet',
    // Officer Management Templates
    form_officer_information: 'Officer Information Collection Form',
    resolution_officer_appointment: 'Officer Appointment Resolution',
    letter_officer_appointment: 'Officer Appointment Letter',
    letter_officer_acceptance: 'Officer Acceptance Letter',
    certificate_officer_appointment: 'Officer Appointment Certificate',
    addendum_officer_duties: 'Officer Duties & Responsibilities Addendum',
    // Resolution Writing Templates
    resolution_format_guide: 'Resolution Format Guide',
    custom_resolution: 'Custom Resolution',
    resolution_filing_certificate: 'Resolution Filing Certificate',
    resolution_distribution_list: 'Resolution Distribution List',
  };

  useEffect(() => {
    loadWorkflowPhases();
    loadGeneratedDocuments();
    loadSignatureRequests();
    loadEmailDeliveries();
    loadTaskWorkflow(); // CRITICAL FIX #2: Load meeting_minutes_id
  }, [workflowId, taskId]);

  // CRITICAL FIX #2: Load task_workflows data including meeting_minutes_id
  const loadTaskWorkflow = async () => {
    const { data } = await supabase
      .from('task_workflows')
      .select('meeting_minutes_id, workflow_data')
      .eq('task_id', taskId)
      .single();
    
    if (data) {
      setMeetingMinutesId(data.meeting_minutes_id);
      // Ensure workflow_data is an object
      const dataObj = typeof data.workflow_data === 'object' && data.workflow_data !== null && !Array.isArray(data.workflow_data)
        ? data.workflow_data as Record<string, any>
        : {};
      setWorkflowData(dataObj);
    }
  };

  // Load templates after phases are populated
  useEffect(() => {
    if (phases && phases.length > 0) {
      loadDocumentTemplates();
    }
  }, [phases]);

  // Real-time signature status updates
  useEffect(() => {
    if (Object.keys(signatureRequests).length === 0) return;

    const signatureRequestIds = Object.values(signatureRequests).map((r: any) => r.id);
    
    const channel = supabase
      .channel(`task-signatures-${taskId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_signers',
        },
        (payload) => {
          // Refresh signature requests when signers update
          loadSignatureRequests();
          loadGeneratedDocuments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [signatureRequests, taskId]);

  // Monitor Phase 2 status for "Conducting a Meeting" workflow
  useEffect(() => {
    if (workflow?.workflow_key === 'conducting_a_meeting' && currentPhase === 2) {
      const updatePhase2Status = async () => {
        try {
          const distComplete = await checkPhase2DistributionComplete();
          const fileComplete = await checkPhase2FilingComplete();
          const blockers = await getPhase2Blockers();
          
          setPhase2DistributionComplete(distComplete);
          setPhase2FilingComplete(fileComplete);
          setPhase2Blockers(blockers);
          
          console.log('📊 Phase 2 Status Update:', {
            distComplete,
            fileComplete,
            blockers,
            canComplete: distComplete && fileComplete
          });
        } catch (error) {
          console.error('Error updating Phase 2 status:', error);
        }
      };
      
      updatePhase2Status();
    }
  }, [generatedDocuments, taskId, workflow?.workflow_key, currentPhase, unaId]);

  // Fetch task user_id for asset transfers workflow
  useEffect(() => {
    const fetchTaskUserId = async () => {
      const { data, error } = await supabase
        .from('custom_tasks')
        .select('user_id')
        .eq('id', taskId)
        .single();
      
      if (!error && data) {
        setTaskUserId(data.user_id);
      }
    };
    
    fetchTaskUserId();
  }, [taskId]);


  const loadWorkflowPhases = async () => {
    try {
      // Get workflow data
      const { data: workflowData, error: workflowError } = await supabase
        .from('workflows')
        .select('phases, workflow_key')
        .eq('id', workflowId)
        .single();

      if (workflowError) throw workflowError;

      setWorkflow(workflowData);

      let phasesArray: Phase[] = [];
      if (workflowData?.phases) {
        phasesArray = (Array.isArray(workflowData.phases) ? workflowData.phases : []) as unknown as Phase[];
        setPhases(phasesArray);
      }

      // Get current phase and statuses from task_workflows
      const { data: taskWorkflow, error: taskWorkflowError } = await supabase
        .from('task_workflows')
        .select('current_phase, workflow_data')
        .eq('task_id', taskId)
        .single();

      if (taskWorkflowError) throw taskWorkflowError;

      if (taskWorkflow) {
        // Extract member data from workflow_data for email pre-population
        const workflowData = taskWorkflow.workflow_data as any;
        if (workflowData && typeof workflowData === 'object') {
          const memberName = workflowData.member_full_legal_name 
            || workflowData.member_full_name 
            || workflowData.member_preferred_name
            || workflowData.new_member_full_name
            || '';
          const memberEmail = workflowData.member_email 
            || workflowData.new_member_email
            || '';
          
          if (memberName || memberEmail) {
            setWorkflowMemberData({ name: memberName, email: memberEmail });
          }
        }
        // Get phase statuses from task_phases
        const { data: taskPhases, error: phasesError } = await supabase
          .from('task_phases')
          .select('phase_number, status')
          .eq('task_id', taskId);

        // Initialize all phases with 'not_started' status
        const statuses: Record<number, string> = {};
        phasesArray.forEach((phase: any) => {
          statuses[phase.phase_number] = 'not_started';
        });

        // Override with actual statuses from database
        if (!phasesError && taskPhases) {
          taskPhases.forEach(p => {
            statuses[p.phase_number] = p.status;
          });
        }
        
        setPhaseStatuses(statuses);

        // Reconcile current phase: align with first incomplete phase
        const firstIncompletePhase = phasesArray.find(
          p => statuses[p.phase_number] !== 'completed'
        )?.phase_number || phasesArray.length;
        
        const reconciledPhase = taskWorkflow.current_phase || 1;
        
        // If current phase is ahead of first incomplete, reconcile it
        if (reconciledPhase > firstIncompletePhase) {
          await supabase
            .from('task_workflows')
            .update({ current_phase: firstIncompletePhase })
            .eq('task_id', taskId);
          setCurrentPhase(firstIncompletePhase);
          setExpandedPhases({ [firstIncompletePhase]: true });
        } else {
          setCurrentPhase(reconciledPhase);
          setExpandedPhases({ [reconciledPhase]: true });
        }
      }
    } catch (error) {
      console.error('Error loading workflow phases:', error);
      toast({
        title: "Error",
        description: "Failed to load workflow phases",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const canAccessPhase = (phaseNumber: number) => {
    // Can access if all prior phases are completed or if it's already completed
    if (phaseStatuses[phaseNumber] === 'completed') return true;
    if (phaseNumber === 1) return true;
    
    // Check all prior phases are completed
    for (let i = 1; i < phaseNumber; i++) {
      if (phaseStatuses[i] !== 'completed') return false;
    }
    return true;
  };

  const getPhaseIcon = (phaseNumber: number) => {
    const status = phaseStatuses[phaseNumber];
    if (status === 'completed') {
      return <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-500" />;
    } else if (phaseNumber === currentPhase) {
      return <Circle className="h-6 w-6 text-primary fill-primary" />;
    } else if (canAccessPhase(phaseNumber)) {
      return <Circle className="h-6 w-6 text-muted-foreground" />;
    } else {
      return <Lock className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const handlePhaseClick = async (phaseNumber: number) => {
    if (!canAccessPhase(phaseNumber)) {
      toast({
        title: "Phase Locked",
        description: "Complete the current phase to unlock this one",
        variant: "destructive",
      });
      return;
    }

    // Update current phase
    const { error } = await supabase
      .from('task_workflows')
      .update({ current_phase: phaseNumber })
      .eq('task_id', taskId);

    if (error) {
      console.error('Error updating phase:', error);
      return;
    }

    setCurrentPhase(phaseNumber);
    setExpandedPhases({ [phaseNumber]: true });
    onPhaseChange(phaseNumber);
  };

  const handleMarkPhaseComplete = async (phaseNumber: number) => {
    console.log('🟢 WorkflowPhaseManager: Received phase complete callback for phase:', phaseNumber);
    try {
      // Check if all requirements are met
      const phase = phases.find(p => p.phase_number === phaseNumber);
      if (!phase) return;

      // Check if phase record already exists
      const { data: existingPhase } = await supabase
        .from('task_phases')
        .select('id')
        .eq('task_id', taskId)
        .eq('phase_number', phaseNumber)
        .single();

      if (existingPhase) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('task_phases')
          .update({ 
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('id', existingPhase.id);

        if (updateError) throw updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('task_phases')
          .insert({ 
            task_id: taskId,
            una_id: unaId,
            phase_number: phaseNumber,
            phase_name: phase.phase_name,
            phase_description: phase.description,
            status: 'completed',
            completed_at: new Date().toISOString(),
            order_number: phaseNumber,
            requirements: phase.requirements || [],
            document_requirements: phase.documents || []
          });

        if (insertError) throw insertError;
      }

      // Update local state immediately for instant UI feedback
      setPhaseStatuses(prev => ({ ...prev, [phaseNumber]: 'completed' }));

      // Refetch phase statuses to ensure consistency
      const { data: updatedPhases } = await supabase
        .from('task_phases')
        .select('phase_number, status')
        .eq('task_id', taskId);
      
      if (updatedPhases) {
        const freshStatuses: Record<number, string> = {};
        phases.forEach(p => {
          freshStatuses[p.phase_number] = 'not_started';
        });
        updatedPhases.forEach(p => {
          freshStatuses[p.phase_number] = p.status;
        });
        setPhaseStatuses(freshStatuses);
      }
      
      const completedCount = updatedPhases?.filter(p => p.status === 'completed').length || 0;
      const totalPhases = phases.length;
      const newProgress = Math.round((completedCount / totalPhases) * 100);
      
      // Update workflow_completion_percentage on the task
      await supabase
        .from('custom_tasks')
        .update({ workflow_completion_percentage: newProgress })
        .eq('id', taskId);
      
      // Update workflow_completion_tracking phases_completed using workflow_id
      await supabase
        .from('workflow_completion_tracking')
        .update({ phases_completed: completedCount })
        .eq('workflow_id', workflowId)
        .eq('una_id', unaId)
        .is('completed_at', null);

      // If Phase 2 just completed, trigger automation
      if (phaseNumber === 2 && workflow?.workflow_key) {
        console.log('Triggering Phase 2 automation...')
        
        const { data, error } = await supabase.functions.invoke('complete-workflow-phase', {
          body: {
            taskId: taskId,
            phaseNumber: 2,
            workflowKey: workflow.workflow_key
          }
        })

        if (error) {
          console.error('Phase 2 automation error:', error)
          toast({
            title: "Warning",
            description: "This phase is done, but the new member was not added for you. You may need to add them by hand.",
            variant: "destructive"
          })
        } else if (data?.success) {
          console.log('Phase 2 automation successful:', data)
          if (data.message) {
            toast({
              title: "Member Added Successfully!",
              description: data.message,
            })
          }
        }
      }

      // CRITICAL FIX #3: If Phase 3 just completed for recording minutes workflow, trigger automation
      if (phaseNumber === 3 && workflow?.workflow_key === 'recording_minutes') {
        console.log('Triggering Phase 3 meeting automation...')
        
        const { data, error } = await supabase.functions.invoke('complete-workflow-phase', {
          body: {
            taskId: taskId,
            phaseNumber: 3,
            workflowKey: workflow.workflow_key
          }
        })

        if (error) {
          console.error('Phase 3 meeting automation error:', error)
          toast({
            title: "Warning",
            description: "This phase is done, but the minutes were not filed for you. You may need to file them by hand.",
            variant: "destructive"
          })
        } else if (data?.success) {
          console.log('Phase 3 meeting automation successful:', data)
          if (data.message) {
            toast({
              title: "Meeting Automation Complete!",
              description: data.message,
            })
          }
        }
      }

      // If Phase 4 just completed for conducting_a_meeting workflow, trigger automation (creates journal entry)
      if (phaseNumber === 4 && workflow?.workflow_key === 'conducting_a_meeting') {
        console.log('Triggering Phase 4 meeting automation for journal entry...')
        
        const { data, error } = await supabase.functions.invoke('complete-workflow-phase', {
          body: {
            taskId: taskId,
            phaseNumber: 4,
            workflowKey: workflow.workflow_key
          }
        })

        if (error) {
          console.error('Phase 4 meeting automation error:', error)
          toast({
            title: "Warning",
            description: "This phase is done, but the journal entry may need to be added by hand.",
            variant: "destructive"
          })
        } else if (data?.success) {
          console.log('Phase 4 meeting automation successful:', data)
          if (data.message) {
            toast({
              title: "Meeting Workflow Complete!",
              description: data.message,
            })
          }
        }
      }

      toast({
        title: "Phase Completed! 🎉",
        description: `${phase.phase_name} has been completed`,
      });

      // Move to next phase if available
      if (phaseNumber < phases.length) {
        const nextPhase = phaseNumber + 1;
        const { error: phaseError } = await supabase
          .from('task_workflows')
          .update({ current_phase: nextPhase })
          .eq('task_id', taskId);

        if (!phaseError) {
          setCurrentPhase(nextPhase);
          // Collapse completed phase, expand next phase
          setExpandedPhases({ [phaseNumber]: false, [nextPhase]: true });
          onPhaseChange(nextPhase);
        }
      } else {
        // All phases complete - mark workflow as completed
        console.log('✅ All phases completed! Marking workflow as completed...');
        
        const now = new Date().toISOString();
        
        // Update task status to completed
        const { error: taskUpdateError } = await supabase
          .from('custom_tasks')
          .update({ 
            status: 'completed',
            completed_at: now
          })
          .eq('id', taskId);
        
        if (taskUpdateError) {
          console.error('Error updating task status:', taskUpdateError);
        }
        
        // Update task_workflows completed_at
        const { error: workflowUpdateError } = await supabase
          .from('task_workflows')
          .update({ completed_at: now })
          .eq('task_id', taskId);
        
        if (workflowUpdateError) {
          console.error('Error updating workflow completed_at:', workflowUpdateError);
        }
        
        // Update workflow_completion_tracking
        await supabase
          .from('workflow_completion_tracking')
          .update({ 
            completed_at: now,
            completion_status: 'completed'
          })
          .eq('workflow_id', workflowId)
          .eq('una_id', unaId)
          .is('completed_at', null);
        
        toast({
          title: "Workflow Completed! 🎉",
          description: "All phases have been completed successfully.",
        });
        
        // Call the callback to refresh UI
        onPhaseComplete();
      }
        
    } catch (error: any) {
      console.error('Error marking phase complete:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to mark phase as complete. Please try again.",
        variant: "destructive",
      });
    }
  };

  const togglePhaseExpanded = (phaseNumber: number) => {
    setExpandedPhases(prev => ({ ...prev, [phaseNumber]: !prev[phaseNumber] }));
  };

  const calculatePhaseProgress = (phase: Phase) => {
    if (!phase.requirements) return 0;
    const completed = phase.requirements.filter(r => r.completed).length;
    return (completed / phase.requirements.length) * 100;
  };

  const handleGenerateDocument = (templateId: string) => {
    setSelectedTemplate(templateId);
    setGenerateDialogOpen(true);
  };

  const loadDocumentTemplates = async () => {
    // Guard: Don't attempt to load if phases aren't ready yet
    if (!phases || phases.length === 0) {
      console.log('⏸️ Skipping template load - phases not ready yet');
      return;
    }

    try {
      // Collect all template keys from ALL phases in current workflow
      const allTemplateKeys = phases.flatMap(phase => 
        (phase.document_requirements || []).map(req => getReqKey(req))
      );

      console.log('🔍 Loading templates for workflow keys:', allTemplateKeys);

      // Fetch templates by explicit keys (no category filtering - supports all workflows)
      const { data: libraryTemplates, error: libraryError } = await supabase
        .from('document_template_library')
        .select('*')
        .in('template_key', allTemplateKeys)
        .eq('is_active', true);

      if (libraryError) {
        console.error('Error loading library templates:', libraryError);
      }

      // Also check legacy templates for backwards compatibility
      const { data: legacyTemplates } = await supabase
        .from('document_templates')
        .select('*')
        .in('template_name', allTemplateKeys);

      // Merge both sources
      const allTemplates = [...(libraryTemplates || []), ...(legacyTemplates || [])];

      const templatesMap: Record<string, any> = {};
      allTemplates.forEach(template => {
        templatesMap[template.template_name] = template;
      });
      setDocumentTemplates(templatesMap);

      // Build quick lookup by template_key
      const keyMap: Record<string, any> = {};
      allTemplates.forEach(template => {
        // Check both possible locations for template_key
        const templateKey = (template as any).template_key || (template as any).template_name;
        if (templateKey) {
          keyMap[templateKey] = template;
        }
      });
      
      // Fallback: match by name from known mappings
      Object.entries(TEMPLATE_KEY_TO_NAME).forEach(([key, name]) => {
        if (!keyMap[key]) {
          const match = allTemplates.find(t => t.template_name === name);
          if (match) keyMap[key] = match;
        }
      });
      
      setTemplatesByKey(keyMap);
      
      // Debug logging
      console.log('✅ Templates loaded by key:', Object.keys(keyMap));
      console.log('❌ Missing templates:', 
        allTemplateKeys.filter(key => !keyMap[key])
      );
      
      // Log template details for debugging
      Object.entries(keyMap).forEach(([key, template]: [string, any]) => {
        console.log(`  ✓ ${key}:`, template.template_name, `(ID: ${template.id})`);
      });
    } catch (error) {
      console.error('Error loading document templates:', error);
    }
  };

  const loadGeneratedDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .select(`
          *,
          template_lib:template_library_id(template_key, template_name),
          template_legacy:template_id(template_name)
        `)
        .eq('task_id', taskId);

      if (error) throw error;

      // Index by BOTH template_key (from relationship) AND document_name (fallback)
      const docsMap: Record<string, any> = {};
      data?.forEach(doc => {
        // Primary: use template_key from library relationship
        if (doc.template_lib?.template_key) {
          docsMap[doc.template_lib.template_key] = doc;
        }
        
        // Fallback: try to match document_name to our template keys
        Object.entries(TEMPLATE_KEY_TO_NAME).forEach(([key, name]) => {
          if (doc.document_name?.includes(name) || doc.document_name === name) {
            docsMap[key] = doc;
          }
        });
        
        // Also index by document_type for legacy compatibility
        if (doc.document_type) {
          docsMap[doc.document_type] = doc;
        }
      });
      
      setGeneratedDocuments(docsMap);
    } catch (error) {
      console.error('Error loading generated documents:', error);
    }
  };

  /**
   * Loads all signature requests for the current task and indexes them by document_id
   * for efficient lookup during rendering. Calculates progress from fresh database data.
   */
  const loadSignatureRequests = async () => {
    try {
      // Get ALL signature requests for this task
      const { data: allRequests, error } = await supabase
        .from('signature_requests')
        .select(`
          *,
          document_signers(*)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('📥 All signature requests:', allRequests);

      // For each document, pick the BEST request
      const bestRequests: Record<string, any> = {};

      for (const request of allRequests || []) {
        const docId = request.document_id;

        // Calculate how many signatures this request has
        const signatures = request.document_signers || [];
        const signedCount = signatures.filter((s: any) => s.status === 'signed').length;
        const totalCount = signatures.length;

        console.log(`  Request ${request.id}: ${request.status}, ${signedCount}/${totalCount} signed`);

        // Add computed counts to request
        const requestWithCounts = {
          ...request,
          signed_count: signedCount,
          total_signers: totalCount,
        };

        const existing = bestRequests[docId];

        if (!existing) {
          // First request for this document
          bestRequests[docId] = requestWithCounts;
        } else {
          // Calculate existing signed count
          const existingSignatures = existing.document_signers || [];
          const existingSignedCount = existingSignatures.filter((s: any) => s.status === 'signed').length;

          // Decide which request is "better"
          const shouldReplace =
            // Prefer completed over anything else
            (request.status === 'completed' && existing.status !== 'completed') ||
            // If both completed or both pending, prefer the one with more signatures
            (request.status === existing.status && signedCount > existingSignedCount) ||
            // If same signatures, prefer newer
            (request.status === existing.status && signedCount === existingSignedCount &&
              new Date(request.created_at) > new Date(existing.created_at));

          if (shouldReplace) {
            console.log(`  ✅ Replacing with better request: ${request.id}`);
            bestRequests[docId] = requestWithCounts;
          }
        }
      }

      console.log('🏆 Best requests selected:', Object.values(bestRequests));

      setSignatureRequests(bestRequests);
    } catch (error) {
      console.error('Error loading signature requests:', error);
    }
  };

  const loadEmailDeliveries = async () => {
    try {
      const documentIds = Object.values(generatedDocuments).map((d: any) => d.id);
      if (documentIds.length === 0) return;
      
      const { data, error } = await supabase
        .from('document_email_log')
        .select('*')
        .in('document_id', documentIds)
        .order('sent_at', { ascending: false });
      
      if (error) throw error;
      
      // Map by document_id (get latest delivery per document)
      const deliveriesMap: Record<string, any> = {};
      data?.forEach((delivery: any) => {
        if (!deliveriesMap[delivery.document_id]) {
          deliveriesMap[delivery.document_id] = delivery;
        }
      });
      
      setEmailDeliveries(deliveriesMap);
    } catch (error) {
      console.error('Error loading email deliveries:', error);
    }
  };

  const handleDocumentGenerated = () => {
    setGenerateDialogOpen(false);
    setSelectedTemplate(null);
    loadGeneratedDocuments(); // Refresh document list
    loadSignatureRequests(); // Also refresh signatures
    
    toast({
      title: "Document Generated! 📄",
      description: "Your document with merge fields has been created",
    });
  };

  /**
   * Signature workflow action handlers
   */
  const handleOpenSignatureWizard = (documentId: string) => {
    setSelectedDocumentForSignature(documentId);
    setSignatureWizardOpen(true);
  };

  const handleSignatureRequestSuccess = () => {
    setSignatureWizardOpen(false);
    setSelectedDocumentForSignature(null);
    loadSignatureRequests();
    loadGeneratedDocuments();
    toast({
      title: "Signature Request Sent",
      description: "Email notifications have been sent to all signers.",
    });
  };

  const handleOpenEmailWizard = (documentId: string) => {
    setSelectedDocumentForEmail(documentId);
    setEmailWizardOpen(true);
  };

  const handleEmailSuccess = () => {
    setEmailWizardOpen(false);
    setSelectedDocumentForEmail(null);
    loadEmailDeliveries();
    toast({
      title: "Document Sent",
      description: "Document has been emailed successfully.",
    });
  };

  const handleDownloadPdf = async (documentId: string, documentName: string) => {
    try {
      setLoading(true);
      toast({ 
        title: 'Generating PDF...', 
        description: 'Using server-side rendering for best quality' 
      });

      // Use shared utility to generate PDF
      const { file_url, file_path, file_size, pdfBlob } = await generateDocumentPdf(
        documentId,
        documentName
      );

      // Update document record with PDF location
      await supabase
        .from('generated_documents')
        .update({
          file_url,
          file_path,
          file_size,
        })
        .eq('id', documentId);

      // Trigger download
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${documentName.replace(/[^a-z0-9\-]+/gi, "-").toLowerCase()}-${documentId}.pdf`;
      link.click();
      URL.revokeObjectURL(downloadUrl);

      toast({
        title: "PDF Ready!",
        description: "Document downloaded successfully",
      });

      // Refresh documents to show updated status
      loadGeneratedDocuments();

    } catch (error: any) {
      console.error('❌ PDF generation error:', error);
      toast({
        title: "PDF Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSignatureTracking = async (signatureRequestId: string) => {
    console.log('📊 Opening signature status, pre-refreshing from DocuSeal...');
    
    // 🆕 Pre-refresh: Trigger refresh before opening modal for instant up-to-date status
    try {
      await supabase.functions.invoke('refresh-docuseal-status', {
        body: { signatureRequestId }
      });
      console.log('✅ Pre-refresh successful');
    } catch (error) {
      console.warn('⚠️ Pre-refresh failed, modal will auto-refresh:', error);
    }
    
    setSelectedSignatureRequestId(signatureRequestId);
    setTrackingDialogOpen(true);
  };

  const handlePreviewDocument = (documentId: string) => {
    setSelectedDocumentIdForPreview(documentId);
    setPreviewDialogOpen(true);
  };

  const handleEditDocument = (documentId: string) => {
    navigate(`/tasks/${taskId}/documents/${documentId}/edit`);
  };

  const handleDownloadDocument = async (documentId: string, documentName: string) => {
    await handleDownloadPdf(documentId, documentName);
  };

  const handleResendRequest = async () => {
    if (!selectedResendDoc) return;
    
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // 1. Get current signature request details
      const { data: currentRequest, error: fetchError } = await supabase
        .from('signature_requests')
        .select('*, document_signers(*)')
        .eq('id', selectedResendDoc.signatureRequestId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // 2. Cancel current signature request
      const { error: cancelError } = await supabase
        .from('signature_requests')
        .update({ 
          status: 'cancelled',
          cancelled_at: new Date().toISOString()
        })
        .eq('id', selectedResendDoc.signatureRequestId);
      
      if (cancelError) throw cancelError;
      
      // 3. Create new signature request
      const { data: newRequest, error: createError } = await supabase
        .from('signature_requests')
        .insert([{
          document_id: selectedResendDoc.documentId,
          una_id: unaId,
          created_by: user.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
        }])
        .select()
        .single();
      
      if (createError) throw createError;
      
      // 4. Copy signers to new request
      const signersToInsert = currentRequest.document_signers.map((signer: any) => ({
        signature_request_id: newRequest.id,
        signer_name: signer.signer_name,
        signer_email: signer.signer_email,
        signer_role: signer.signer_role,
        docuseal_role: signer.docuseal_role,
        status: 'pending'
      }));
      
      const { error: signersError } = await supabase
        .from('document_signers')
        .insert(signersToInsert);
      
      if (signersError) throw signersError;
      
      // 5. Create new DocuSeal submission
      const { error: submissionError } = await supabase.functions.invoke(
        'create-docuseal-submission',
        {
          body: {
            signatureRequestId: newRequest.id
          }
        }
      );
      
      if (submissionError) throw submissionError;
      
      toast({
        title: "Signature request re-sent",
        description: "New signature invitations have been sent to all signers.",
      });
      
      // Refresh data
      await loadSignatureRequests();
      await loadGeneratedDocuments();
      setResendConfirmOpen(false);
      setSelectedResendDoc(null);
      
    } catch (error: any) {
      console.error('Error resending signature request:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to re-send signature request",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get phase signature requirements - checks which documents in a phase need signatures
   */
  const getPhaseSignatureRequirements = (phaseNumber: number): Record<string, any> => {
    const phase = phases.find(p => p.phase_number === phaseNumber);
    if (!phase?.document_requirements) return {};
    
    const requirements: Record<string, any> = {};
    
    phase.document_requirements.forEach(req => {
      const templateKey = getReqKey(req);
      const generatedDoc = generatedDocuments[templateKey];
      if (!generatedDoc) return;
      
      const sigRequest = signatureRequests[generatedDoc.id];
      if (sigRequest) {
        requirements[templateKey] = sigRequest;
      }
    });
    
    return requirements;
  };

  /**
   * Check Phase 2 distribution completion for "Conducting a Meeting" workflow
   */
  const checkPhase2DistributionComplete = async (): Promise<boolean> => {
    if (!taskId || workflow?.workflow_key !== 'conducting_a_meeting') return true;
    
    try {
      // Get finalized document IDs
      const documentIds = Object.values(generatedDocuments)
        .filter(doc => doc.status === 'final' || doc.status === 'finalized')
        .map(doc => doc.id);
      
      if (documentIds.length === 0) return false;
      
      // Check if at least one document has been distributed
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('id')
        .in('document_id', documentIds)
        .eq('status', 'sent')
        .limit(1);
      
      if (error) {
        console.error('Error checking distribution:', error);
        return false;
      }
      
      return (data?.length ?? 0) > 0;
    } catch (err) {
      console.error('Error in checkPhase2DistributionComplete:', err);
      return false;
    }
  };
  
  /**
   * Check Phase 2 filing completion for "Conducting a Meeting" workflow
   */
  const checkPhase2FilingComplete = async (): Promise<boolean> => {
    if (!taskId || workflow?.workflow_key !== 'conducting_a_meeting') return true;
    
    try {
      // Get finalized document IDs
      const documentIds = Object.values(generatedDocuments)
        .filter(doc => doc.status === 'final' || doc.status === 'finalized')
        .map(doc => doc.id);
      
      if (documentIds.length === 0) return false;
      
      // Check if all documents have been filed to governance
      const { data, error } = await supabase
        .from('governance_documents')
        .select('id, original_file_url')
        .eq('una_id', unaId)
        .eq('generated_from_task_id', taskId);
      
      if (error) {
        console.error('Error checking filing:', error);
        return false;
      }
      
      // All finalized documents should be filed
      return (data?.length ?? 0) >= documentIds.length;
    } catch (err) {
      console.error('Error in checkPhase2FilingComplete:', err);
      return false;
    }
  };
  
  /**
   * Get Phase 2 blockers for "Conducting a Meeting" workflow
   */
  const getPhase2Blockers = async (): Promise<string[]> => {
    if (!taskId || workflow?.workflow_key !== 'conducting_a_meeting') return [];
    
    const blockers: string[] = [];
    
    try {
      const distComplete = await checkPhase2DistributionComplete();
      const fileComplete = await checkPhase2FilingComplete();
      
      if (!distComplete) {
        blockers.push('Distribute at least one document to meeting participants');
      }
      
      if (!fileComplete) {
        blockers.push('File all finalized documents to governance records');
      }
    } catch (err) {
      console.error('Error in getPhase2Blockers:', err);
      blockers.push('Unable to verify completion status');
    }
    
    return blockers;
  };

  /**
   * Check if phase can be marked complete - validates documents are generated
   * Signatures are optional - users can complete with generated docs and sign manually
   */
  const canMarkPhaseComplete = (phaseNumber: number): boolean => {
    const phase = phases.find(p => p.phase_number === phaseNumber);
    if (!phase) return false;
    
    // Special handling for "Conducting a Meeting" Phase 2
    if (workflow?.workflow_key === 'conducting_a_meeting' && phaseNumber === 2) {
      return phase2DistributionComplete && phase2FilingComplete;
    }
    
    // Phase is completable when all required documents are generated
    // Signatures are optional - users can choose to:
    // 1. Complete now with generated docs (sign manually/print), OR
    // 2. Request signatures via DocuSeal, then complete when signed
    const allDocsGenerated = phase.document_requirements?.every(
      req => generatedDocuments[getReqKey(req)] !== undefined
    ) ?? true;
    
    console.log('🔍 Phase completion check:', {
      phaseNumber,
      phaseName: phase.phase_name,
      allDocsGenerated,
      documents: Object.keys(generatedDocuments)
    });
    
    return allDocsGenerated;
  };

  /**
   * Get list of what's blocking phase completion
   */
  const getPhaseCompletionBlockers = (phaseNumber: number): string[] => {
    const blockers: string[] = [];
    const phase = phases.find(p => p.phase_number === phaseNumber);
    if (!phase) return blockers;
    
    // Special handling for "Conducting a Meeting" Phase 2
    if (workflow?.workflow_key === 'conducting_a_meeting' && phaseNumber === 2) {
      return phase2Blockers;
    }
    
    // Only block on missing documents, not signatures
    // Users can complete phase with generated docs even if not signed
    phase.document_requirements?.forEach(req => {
      const templateKey = getReqKey(req);
      const templateName = getReqName(req, TEMPLATE_KEY_TO_NAME);
      if (!generatedDocuments[templateKey]) {
        blockers.push(`Generate "${templateName}"`);
      }
    });
    
    return blockers;
  };

  /**
   * Check if document requires signatures based on workflow configuration
   */
  const doesDocumentRequireSignatures = (templateKey: string, phaseNumber: number): boolean => {
    console.log('🔍 Checking signature requirement for:', templateKey, 'in phase', phaseNumber);
    
    // Find the current phase
    const phase = phases?.find(p => p.phase_number === phaseNumber);
    
    if (!phase) {
      console.warn('⚠️ Phase not found:', phaseNumber);
      return true; // Default to requiring signatures if phase not found
    }
    
    // Find the document requirement
    const requirement = phase.document_requirements?.find(req => {
      const reqKey = typeof req === 'string' ? req : req.template_key;
      return reqKey === templateKey;
    });
    
    if (!requirement) {
      console.warn('⚠️ Document requirement not found:', templateKey);
      return true; // Default to requiring signatures if not in requirements
    }
    
    // Check if requirement specifies requires_signature
    const requiresSig = typeof requirement === 'object' && 'requires_signature' in requirement
      ? requirement.requires_signature
      : true; // Default to true if not specified
    
    console.log(`✅ ${templateKey}: requires_signature =`, requiresSig);
    
    return requiresSig;
  };

  /**
   * Enhanced document status that includes signature requirements
   */
  const getDocumentStatus = (templateKey: string, phaseNumber: number): import('@/components/workflows/types/workflow-components.types').DocumentStatus => {
    const doc = generatedDocuments[templateKey];
    const sigRequest = doc ? signatureRequests[doc.id] : null;
    const emailDelivery = doc ? emailDeliveries[doc.id] : null;
    const requiresSigs = doesDocumentRequireSignatures(templateKey, phaseNumber);
    
    if (!doc) {
      return { 
        status: 'not_generated' as const, 
        label: 'Not Generated',
        color: 'text-muted-foreground',
        icon: Circle,
        requiresSignatures: requiresSigs
      };
    }
    
    // Check if this document has an active signature request
    if (sigRequest) {
      if (sigRequest.status === 'completed') {
        return {
          status: 'fully_signed' as const,
          label: 'Signed',
          color: 'text-green-600 dark:text-green-500',
          icon: CheckCircle2,
          requiresSignatures: true,
          signatureData: sigRequest
        };
      }
      
      return {
        status: 'pending_signatures' as const,
        label: 'Awaiting Signatures',
        color: 'text-blue-600 dark:text-blue-500',
        icon: Users,
        requiresSignatures: true,
        signatureData: sigRequest,
        canRequestSignatures: false
      };
    }
    
    // Check if document was emailed (for non-signature documents)
    if (!requiresSigs && emailDelivery) {
      return {
        status: 'emailed' as const,
        label: 'Sent',
        color: 'text-green-600 dark:text-green-500',
        icon: Mail,
        requiresSignatures: false,
        emailData: emailDelivery
      };
    }
    
    // Document generated but no signature request or email yet
    return {
      status: 'generated' as const,
      label: 'Generated',
      color: 'text-blue-600 dark:text-blue-500',
      icon: CheckCircle2,
      requiresSignatures: requiresSigs,
      canRequestSignatures: requiresSigs,
      canEmailDocument: !requiresSigs
    };
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading phases...</div>;
  }

  const overallProgress = phases.length > 0
    ? (Object.values(phaseStatuses).filter(s => s === 'completed').length / phases.length) * 100
    : 0;

  return (
    <div className="space-y-4">
      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Workflow Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overall Completion</span>
            <span className="font-medium">{Math.round(overallProgress)}%</span>
          </div>
          <Progress value={overallProgress} className="h-2" />
          <div className="text-xs text-muted-foreground">
            {Object.values(phaseStatuses).filter(s => s === 'completed').length} of {phases.length} phases completed
          </div>
        </CardContent>
      </Card>

      {/* Phase List */}
      <div className="space-y-3">
        {phases.map((phase) => {
          const isCompleted = phaseStatuses[phase.phase_number] === 'completed';
          const isCurrent = phase.phase_number === currentPhase;
          const isLocked = !canAccessPhase(phase.phase_number);
          const isExpanded = expandedPhases[phase.phase_number];

          return (
            <Card 
              key={phase.phase_number}
              className={cn(
                "transition-all",
                isCurrent && "ring-2 ring-primary",
                isCompleted && "bg-green-50 dark:bg-green-950/20"
              )}
            >
              <Collapsible open={isExpanded} onOpenChange={() => togglePhaseExpanded(phase.phase_number)}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => togglePhaseExpanded(phase.phase_number)}
                    >
                      {getPhaseIcon(phase.phase_number)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">Phase {phase.phase_number}: {phase.phase_name}</h3>
                          {isCurrent && !isCompleted && (
                            <Badge variant="default" className="text-xs">Current</Badge>
                          )}
                          {isCompleted && (
                            <Badge className="text-xs bg-green-600 hover:bg-green-700 text-white">✓ Completed</Badge>
                          )}
                          {isLocked && (
                            <Badge variant="outline" className="text-xs">Locked</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{phase.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{phase.estimated_time}</span>
                          </div>
                          {phase.documents && phase.documents.length > 0 && (
                            <div className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              <span>{phase.documents.length} document{phase.documents.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {phase.training_videos && phase.training_videos.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Video className="h-3 w-3" />
                              <span>{phase.training_videos.length} video{phase.training_videos.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Hide Complete button for conducting_a_meeting Phase 2 - concluding meeting auto-completes */}
                      {isCurrent && !isCompleted && !(workflow?.workflow_key === 'conducting_a_meeting' && phase.phase_number === 2) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (canMarkPhaseComplete(phase.phase_number)) {
                                      handleMarkPhaseComplete(phase.phase_number);
                                    } else {
                                      toast({
                                        title: "Phase Incomplete",
                                        description: "Finish every document and collect every signature first, then you can close this phase.",
                                        variant: "destructive"
                                      });
                                    }
                                  }}
                                  size="sm"
                                  disabled={!canMarkPhaseComplete(phase.phase_number)}
                                  className="gap-1.5"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                  Complete
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!canMarkPhaseComplete(phase.phase_number) && (
                              <TooltipContent className="max-w-xs">
                                <p className="font-medium mb-1">Requirements not met:</p>
                                <ul className="text-xs space-y-0.5">
                                  {getPhaseCompletionBlockers(phase.phase_number).map((blocker, idx) => (
                                    <li key={idx}>• {blocker}</li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                </CardHeader>

                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <Separator />

                    {/* CRITICAL FIX #2 & #7: Custom interfaces for recording_minutes workflow */}
                    {workflow?.workflow_key === 'recording_minutes' && phase.phase_number === 1 && meetingMinutesId ? (
                      <MeetingRecordingInterface
                        meetingId={meetingMinutesId}
                        unaId={unaId}
                        taskId={taskId}
                        initialData={workflowData}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'recording_minutes' && phase.phase_number === 2 && meetingMinutesId ? (
                      <MeetingMinutesReview
                        meetingId={meetingMinutesId}
                        taskId={taskId}
                        unaId={unaId}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'recording_minutes' && phase.phase_number === 3 && meetingMinutesId ? (
                      <DistributeAndArchivePhase
                        taskId={taskId}
                        unaId={unaId}
                        meetingMinutesId={meetingMinutesId}
                        task={{ workflow_data: workflowData }}
                        onPhaseComplete={() => handleMarkPhaseComplete(3)}
                      />
                    ) : workflow?.workflow_key === 'asset_transfers_contributions' && phase.phase_number === 1 ? (
                      <>
                        {console.log('🔍 Passing data to AssetTransfersPhase1:', {
                          taskId,
                          unaId,
                          taskUserId,
                          workflowData,
                          hasWorkflowData: Object.keys(workflowData).length > 0
                        })}
                        <AssetTransfersPhase1
                          task={{ 
                            id: taskId, 
                            una_id: unaId, 
                            user_id: taskUserId || null,
                            workflow_data: workflowData 
                          }}
                          phaseData={phase}
                          onPhaseComplete={() => handleMarkPhaseComplete(1)}
                        />
                      </>
                    ) : workflow?.workflow_key === 'asset_transfers_contributions' && phase.phase_number === 2 ? (
                      <AssetTransfersPhase2
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'asset_transfers_contributions' && phase.phase_number === 3 ? (
                      <>
                        {/* Phase 3: Record & Update for Asset Transfers */}
                        <AssetTransfersPhase3
                          taskId={taskId}
                          unaId={unaId}
                          task={{ 
                            id: taskId, 
                            una_id: unaId, 
                            user_id: taskUserId || null,
                            workflow_data: workflowData 
                          }}
                          phaseData={phase}
                          onPhaseComplete={() => handleMarkPhaseComplete(3)}
                        />
                      </>
                    ) : workflow?.workflow_key === 'conducting_a_meeting' && phase.phase_number === 2 ? (
                      <ConductMeetingPhase2
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'conducting_a_meeting' && phase.phase_number === 3 ? (
                      <ConductMeetingPhase3
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(3)}
                      />
                    ) : workflow?.workflow_key === 'conducting_a_meeting' && phase.phase_number === 4 ? (
                      <ConductMeetingPhase4
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(4)}
                      />
                    ) : workflow?.workflow_key === 'conducting_a_meeting' && phase.phase_number === 1 ? (
                      <ConductingMeetingPhase1
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'add_officer' && phase.phase_number === 1 ? (
                      <OfficerPhase1
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'add_officer' && phase.phase_number === 2 ? (
                      <OfficerPhase2
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'add_officer' && phase.phase_number === 3 ? (
                      <OfficerPhase3
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(3)}
                      />
                    ) : workflow?.workflow_key === 'add_member_simple' && phase.phase_number === 1 ? (
                      <MemberPhase1
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'add_member_simple' && phase.phase_number === 2 ? (
                      <MemberPhase2
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'add_member_simple' && phase.phase_number === 3 ? (
                      <MemberPhase3
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(3)}
                      />
                    ) : workflow?.workflow_key === 'writing_resolution' && phase.phase_number === 1 ? (
                      <ResolutionPhase1
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'writing_resolution' && phase.phase_number === 2 ? (
                      <ResolutionPhase2
                        taskId={taskId}
                        unaId={unaId}
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'execute_religious_covenant' && phase.phase_number === 1 ? (
                      <CovenantPhase1
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'execute_religious_covenant' && phase.phase_number === 2 ? (
                      <CovenantPhase2
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'establish_center_occupancy' && phase.phase_number === 1 ? (
                      <HousingPhase1
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'establish_center_occupancy' && phase.phase_number === 2 ? (
                      <HousingPhase2
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'give_into_stewardship' && phase.phase_number === 1 ? (
                      <GivingPhase1
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'give_into_stewardship' && phase.phase_number === 2 ? (
                      <GivingPhase2
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'conclude_membership' && phase.phase_number === 1 ? (
                      <ExitPhase1
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'conclude_membership' && phase.phase_number === 2 ? (
                      <ExitPhase2
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : workflow?.workflow_key === 'appoint_officer' && phase.phase_number === 1 ? (
                      <AppointPhase1
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'appoint_officer' && phase.phase_number === 2 ? (
                      <AppointPhase2
                        task={{
                          id: taskId,
                          una_id: unaId,
                          user_id: taskUserId || null,
                          workflow_data: workflowData
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />



                    ) : workflow?.workflow_key === 'record_journal_transaction' && phase.phase_number === 1 ? (
                      <RecordJournalPhase1
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(1)}
                      />
                    ) : workflow?.workflow_key === 'record_journal_transaction' && phase.phase_number === 2 ? (
                      <RecordJournalPhase2
                        task={{ 
                          id: taskId, 
                          una_id: unaId, 
                          user_id: taskUserId || null,
                          workflow_data: workflowData 
                        }}
                        phaseData={phase}
                        onPhaseComplete={() => handleMarkPhaseComplete(2)}
                      />
                    ) : (
                      <>
                        {/* Standard phase rendering for all other workflows */}
                        
                        {/* Tasks */}
                        {phase.tasks && phase.tasks.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Steps to Complete:</h4>
                        <ul className="space-y-1.5 text-sm">
                          {phase.tasks.map((task, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-muted-foreground mt-0.5">•</span>
                              <span>{task}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Document Requirements Section */}
                    {phase.document_requirements && phase.document_requirements.length > 0 && (
                      <DocumentRequirementsGrid
                        requirements={phase.document_requirements.map(normalizeRequirement)}
                        generatedDocuments={generatedDocuments}
                        signatureRequests={signatureRequests}
                        emailDeliveries={emailDeliveries}
                        templatesByKey={templatesByKey}
                        templateKeyToName={TEMPLATE_KEY_TO_NAME}
                        phaseNumber={phase.phase_number}
                        loading={loading}
                        getDocumentStatus={getDocumentStatus}
                        onGenerateDocument={handleGenerateDocument}
                        onEditDocument={handleEditDocument}
                        onPreviewDocument={handlePreviewDocument}
                        onDownloadDocument={handleDownloadDocument}
                        onRequestSignatures={handleOpenSignatureWizard}
                        onEmailDocument={handleOpenEmailWizard}
                        onResendSignatureRequest={handleResendRequest}
                        onViewSignatureStatus={handleViewSignatureTracking}
                      />
                    )}

                    {/* Legacy documents support (for backwards compatibility) */}
                    {phase.documents && phase.documents.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Additional Documents:</h4>
                        <div className="space-y-2">
                          {phase.documents.map((doc, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{doc.title}</span>
                                {doc.required && (
                                  <Badge variant="secondary" className="text-xs">Required</Badge>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleGenerateDocument(doc.template_id)}
                                className="gap-1.5 text-xs h-7"
                              >
                                <FileText className="h-3 w-3" />
                                Generate
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions - Enhanced with signature validation */}
                    {isCurrent && !isCompleted && (
                      <div className="flex gap-2 pt-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="w-full">
                                <Button 
                                  onClick={() => {
                                    if (canMarkPhaseComplete(phase.phase_number)) {
                                      handleMarkPhaseComplete(phase.phase_number);
                                    } else {
                                      toast({
                                        title: "Phase Incomplete",
                                        description: "Please complete all documents and collect all signatures.",
                                        variant: "destructive"
                                      });
                                    }
                                  }}
                                  disabled={!canMarkPhaseComplete(phase.phase_number)}
                                  className="gap-2 w-full"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                  Mark Phase Complete
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!canMarkPhaseComplete(phase.phase_number) && (
                              <TooltipContent className="max-w-xs">
                                <p className="font-medium mb-1">Requirements not met:</p>
                                <ul className="text-xs space-y-0.5">
                                  {getPhaseCompletionBlockers(phase.phase_number).map((blocker, idx) => (
                                    <li key={idx}>• {blocker}</li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                    {/* Close the fragment for standard phase rendering */}
                    </>
                  )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Document Generation Dialog */}
      {selectedTemplate && (
        <DocumentGenerateDialog
          open={generateDialogOpen}
          onClose={() => {
            setGenerateDialogOpen(false);
            setSelectedTemplate(null);
          }}
          taskId={taskId}
          unaId={unaId}
          taskTitle="Workflow Document"
          categoryType="governance"
          onGenerated={handleDocumentGenerated}
          initialTemplateId={selectedTemplate}
          phaseRequirement={selectedTemplate ? {
            template_key: selectedTemplate,
            auto_generate: true,
            requires_signature: false
          } : undefined}
          autoSubmit={true}
        />
      )}

      {/* Signature Request Wizard */}
      <SignatureRequestWizard
        open={signatureWizardOpen}
        onOpenChange={setSignatureWizardOpen}
        taskId={taskId}
        unaId={unaId}
        preSelectedDocumentId={selectedDocumentForSignature || undefined}
        onSuccess={handleSignatureRequestSuccess}
      />

      {/* Signature Tracking Dialog */}
      <SignatureTrackingDialog
        open={trackingDialogOpen}
        onOpenChange={setTrackingDialogOpen}
        signatureRequestId={selectedSignatureRequestId || ''}
      />

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        documentId={selectedDocumentIdForPreview}
      />

      {/* Re-send Confirmation Dialog */}
      <AlertDialog open={resendConfirmOpen} onOpenChange={setResendConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-send Signature Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the current signature request and send new invitations to all signers.
              Any partially completed signatures will need to be re-signed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResendRequest}>
              Re-send Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document Email Wizard */}
      {selectedDocumentForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={setEmailWizardOpen}
          taskId={taskId}
          unaId={unaId}
          preSelectedDocumentId={selectedDocumentForEmail}
          prePopulatedRecipient={workflowMemberData}
          onSuccess={handleEmailSuccess}
        />
      )}
    </div>
  );
};
